/*
 * File: Comment.jsx
 * Project: our-wave-stories-app
 *
 * Created by Brendan Michaelsen on February 4, 2022 at 4:30 PM
 * Copyright © 2022 Our Wave, Inc. All rights reserved.
 *
 * Last Modified: June 2, 2025 at 12:16 PM
 * Modified By: Brendan Michaelsen
 */

/**
 * Imports
 */

// Modules
import React, {
	memo, useContext, useEffect, useRef, useState
} from 'react';
import PropTypes from 'prop-types';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Trans, useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

// Utilities
import { formatToRelativeTime } from '../../../utilities/dateTime';

// Context
import ActionContext from '../../context/SharedActionContext';

// Components
import { Typography } from '../Typography';
import { ContentLocalityBadge } from '../ContentLocalityBadge';
import { CommentCreator } from '../CommentCreator';

// Constants
import { AVAILABLE_I18N_NAMESPACES, SOURCES } from '../../../constants/base.constants';

// Styles
import * as S from './Comment.styles';


/**
 * Component
 */

export const Comment = memo(({
	className, comment, source, sourceId, postCreateActions
}) => {

	// Get context from navigation parent
	const {
		dispatch: actionDispatch
	} = useContext(ActionContext);

	// Get current locale from hook
	const locale = useSelector((state) => state.locale.value);

	// Create state
	const [showOptionsDropdown, setShowOptionsDropdown] = useState(false);
	const [showCommentCreator, setShowCommentCreator] = useState(false);

	// Create refs
	const showOptionsDropdownRef = useRef();
	const containerRef = useRef();

	// Get translation hook
	const [t] = useTranslation(AVAILABLE_I18N_NAMESPACES.CLIENT);

	// Handle report comment
	const reportComment = () => {

		// Open report modal
		actionDispatch({
			type: 'OPEN_REPORT_MODAL',
			payload: {
				source: SOURCES.COMMENT,
				sourceId: comment?.comment_id
			}
		});
	};

	// Toggle navigation visibility
	const toggleNavigation = () => {
		setShowOptionsDropdown(!showOptionsDropdown);
	};

	// Handle actions on ref change
	useEffect(() => {

		// Define click outside function
		function handleClickOutside(event) {
			if (showOptionsDropdownRef.current && containerRef.current && !containerRef.current.contains(event.target)) {
				setShowOptionsDropdown(false);
			}
		}

		// Bind the event listener
		document.addEventListener('mousedown', handleClickOutside);

		// Return effect
		return () => { // Unbind the event listener on clean up
			document.removeEventListener('mousedown', handleClickOutside);
		};

	}, [containerRef]);

	// Handle actions on dropdown state
	useEffect(() => {

		// Update ref
		showOptionsDropdownRef.current = showOptionsDropdown;

	}, [showOptionsDropdown]);

	// Handle actions on component load
	useEffect(() => {}, []);

	// Render component
	return (
		<S.Wrapper className={className}>
			<S.CommentWrapper>

				{/* Author Container */}
				<S.AuthorContainer>
					{/* <Typography tag="p" weight="bold" variation="3">{comment?.user.screenname}</Typography> */}
					{/* <Typography tag="p" variation="3">•</Typography> */}
					<Typography tag="p" variation="3">{formatToRelativeTime(comment?.created_at?.iso ? new Date(comment?.created_at?.iso) : new Date(), locale.localeId)}</Typography>
					<ContentLocalityBadge country={comment?.submitted_country} id={comment?.comment_id} />
				</S.AuthorContainer>

				{/* Comment Container */}
				<S.CommentContainer>
					<Typography tag="p" variation="2">{comment?.content}</Typography>
				</S.CommentContainer>

				{/* Actions */}
				<S.Actions>

					{/* Action - Reply */}
					{comment?.depth < 3 && (
						<S.CommentAction className="animate" onClick={() => { setShowCommentCreator(true); }}>
							<FontAwesomeIcon icon={['far', 'arrow-turn-left']} />
							<Typography tag="span">
								<Trans t={t} i18nKey="actions.reply">
									Reply
								</Trans>
							</Typography>
						</S.CommentAction>
					)}

					{/* Action - Options */}
					<S.DropdownContainer className={className} ref={containerRef}>

						{/* Dropdown Toggle */}
						<S.CommentAction className="animate" onClick={toggleNavigation}>
							<FontAwesomeIcon icon={['far', 'ellipsis']} />
						</S.CommentAction>

						{/* Dropdown */}
						<S.OptionsDropdown className={showOptionsDropdown ? 'animate show' : 'animate'}>
							<S.NavigationItem className="animate" onClick={() => { reportComment(); toggleNavigation(); }}>
								<S.IconContainer>
									<FontAwesomeIcon icon={['far', 'flag']} />
								</S.IconContainer>
								<Typography variation="2" tag="p" weight="regular">{t('actions.report')}</Typography>
							</S.NavigationItem>
						</S.OptionsDropdown>
					</S.DropdownContainer>
				</S.Actions>
			</S.CommentWrapper>

			{/* Comment Creator */}
			{showCommentCreator && (
				<S.CreatorContainer>
					<CommentCreator
						initialCompact={false}
						source={source}
						sourceId={sourceId}
						replyId={comment.comment_id}
						onClose={() => { setShowCommentCreator(false); }}
						postCreateActions={(commentObj) => { postCreateActions(commentObj); }}
					/>
				</S.CreatorContainer>
			)}

		</S.Wrapper>
	);
});


/**
 * Configuration
 */

Comment.displayName = 'Comment';
Comment.propTypes = {
	className: PropTypes.string,
	comment: PropTypes.shape(),
	depth: PropTypes.number,
	source: PropTypes.oneOf([SOURCES.STORY, SOURCES.QUESTION, SOURCES.MESSAGE]),
	sourceId: PropTypes.string,
	postCreateActions: PropTypes.func
};
Comment.defaultProps = {
	className: null,
	comment: null,
	depth: 0,
	source: null,
	sourceId: null,
	postCreateActions: null
};
